import { Component, ElementRef, output, signal, ViewChild, Inject, Optional } from "@angular/core";
import { DropdownAlign } from "@radix-ng/primitives/dropdown-menu";
import { CellContext, HeaderContext, injectFlexRenderContext } from "@tanstack/angular-table";
import { Delete, Ellipsis, Loader, LucideAngularModule, View, Clipboard } from "lucide-angular";
import { DynamicOverlayConfig, OverlayService, OVERLAY_DATA } from "~/components/overlay.service";
import { OriButton } from "~/components/ui/button";
import { OriDropdownMenuContent, OriDropdownMenuContent2, OriDropdownMenuItem, OriDropdownMenuItem2, OriDropdownMenuLabel, OriDropdownMenuLabel2, OriDropdownMenuSeparator, OriDropdownMenuTrigger } from "~/components/ui/dropdown-menu";
import { PreviewDto } from "~/src/app/Models/Table";
import { PreviewDialogComponent } from "./preview-dialog.component";
import { OverlayRef } from "@angular/cdk/overlay";

@Component({
  imports: [
    OriButton,
    OriDropdownMenuTrigger,
    LucideAngularModule,
    OriDropdownMenuContent,
    OriDropdownMenuItem,
    OriDropdownMenuLabel,
    OriDropdownMenuSeparator,
  ],
  template: `
    <button
      class="shadow-md h-[20px] w-[20px] hover:bg-blue-100"
      #triggerButton 
      (click)="openDropdown()"
      oriButton
      variant="ghost"
      size="icon"
      aria-label="Open edit menu"
    >
      <lucide-angular [img]="Ellipsis" size="16" strokeWidth="3" aria-hidden="true"/>
    </button>
  `,
  standalone: true,
})
export class ActionRow<T> {
  contextHeader = injectFlexRenderContext<HeaderContext<T, unknown>>();
  contextRow = injectFlexRenderContext<CellContext<T, unknown>>();
  readonly actionPreviewData = signal<PreviewDto[]>([]);
  
  @ViewChild('triggerButton', {read: ElementRef})
  triggerButton!: ElementRef;

  protected readonly DropdownAlign = DropdownAlign;
  protected readonly Ellipsis = Ellipsis;
  protected readonly View = View;
  protected readonly Delete = Delete;
  protected readonly Loader = Loader;
  protected readonly Clipboard = Clipboard;
  readonly blur = output<boolean>();
  show = false;


  constructor(private overlayService: OverlayService) {
  }

  createPreviewData(){
    const arr: PreviewDto[] = [];
    // Add validation
    if (!this.contextHeader.table.getAllLeafColumns() || this.contextHeader.table.getAllLeafColumns().length === 0) {
      console.error('No columns available');
      return;
    }

    if (!this.contextRow.row) {
      console.error('No row data available');
      return;
    }
    this.contextHeader.table.getAllLeafColumns().map((c) => {
      if (typeof c.columnDef.header === "string" && c.id !== 'action' && c.id !== 'select') {
        arr.push({
          id: c.id,
          header: c.columnDef.header,
          cellValue: this.contextRow.row.getValue(c.id),
          cellType: c.columnDef.meta?.colType,
        })
      }
    });
    this.actionPreviewData.set(arr);
  }

  openDropdown() {
    const overlayConfig: DynamicOverlayConfig = {
      relativeTo: this.triggerButton,
      offsetY: 5,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop', 
      width: '200px',
      panelClass: 'dropdown-panel',
      scrollStrategy: 'reposition',
      disableClose: false // Allow closing on backdrop click
    };
    
    // Pass the parent component instance to the dropdown so it can call methods
    const dropdownData = {
      parentComponent: this,
      actionPreviewData: this.actionPreviewData()
    };
    
    this.overlayService.open(ActionRowDropdown, overlayConfig, dropdownData);
  }

  openModal() {
    const overlayConfig: DynamicOverlayConfig = {
      position: 'center',
      hasBackdrop: true,
      width: '600px',
      maxHeight: '80vh',
      panelClass: 'modal-panel',
      scrollStrategy: 'block',
    };
    this.overlayService.open(PreviewDialogComponent, overlayConfig, this.actionPreviewData());
  }

  previewClick() {
    this.createPreviewData();
    this.openModal();
    this.blur.emit(true);
  }

  handleSoftDel() {
    const rowIndex = +this.contextRow.row.id;
    const value = true;
    const columnId = "softDel";
    this.contextHeader.table.options.meta?.delRow(rowIndex, columnId, value);
  }

  copyEntireRow() {
    this.createPreviewData();
    const rowData = this.actionPreviewData().map(item => 
      `${item.header}: ${item.cellValue}`
    ).join('\n');
    
    navigator.clipboard.writeText(rowData).then(() => {
      console.log('Row copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy row: ', err);
    });
  }
}

@Component({
  selector: 'app-action-row-dropdown',
  imports: [
    LucideAngularModule,
    OriDropdownMenuSeparator,
    OriDropdownMenuContent2,
    OriDropdownMenuItem2,
    OriDropdownMenuLabel2
  ],
  template: `
    <!-- Remove ng-template wrapper - this was preventing rendering -->
    <div oriDropdownMenuContent2  class="bg-white border border-gray-200 rounded-md shadow-lg">
      <div oriDropdownMenuLabel2 class="font-md px-2 py-1">Actions</div>

      <!-- Preview button -->
      <div oriDropdownMenuItem2
        class="flex items-center hover:bg-blue-100 justify-between w-full px-2 py-1 cursor-pointer"

        (click)="onPreviewClick()"
      >
        <span>Preview</span>
        <lucide-angular class="ml-auto" [img]="View" size="16" strokeWidth="2" />
      </div>

      <!-- Copy button -->
      <div oriDropdownMenuItem2
        class="flex items-center hover:bg-blue-100 justify-between w-full px-2 py-1 cursor-pointer"
        
        (click)="onCopyClick()"
      >
        <span>Copy Entire Row</span>
        <lucide-angular class="ml-auto" [img]="Clipboard" size="16" strokeWidth="2"/>
      </div>

      <div oriDropdownMenuSeparator></div>

      <!-- Delete button -->
      <div oriDropdownMenuItem2
        class="flex items-center justify-between hover:bg-red-100 w-full px-2 py-1 cursor-pointer text-red-600"
        
        (click)="onDeleteClick()"
      >
        <span>Delete</span>
        <lucide-angular class="ml-auto" [img]="Delete" size="16" strokeWidth="2"/>
      </div>
    </div>
  `,
  standalone: true,
})
class ActionRowDropdown {
  readonly Delete = Delete;
  readonly Clipboard = Clipboard;
  readonly View = View;

  constructor(
    @Optional() private overlayRef: OverlayRef,
    @Optional() @Inject(OVERLAY_DATA) public data: any
  ) {}

  onPreviewClick() {
    if (this.data?.parentComponent) {
      this.data.parentComponent.previewClick();
    }
    this.closeDropdown();
  }

  onCopyClick() {
    if (this.data?.parentComponent) {
      this.data.parentComponent.copyEntireRow();
    }
    this.closeDropdown();
  }

  onDeleteClick() {
    if (this.data?.parentComponent) {
      this.data.parentComponent.handleSoftDel();
    }
    this.closeDropdown();
  }

  private closeDropdown() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }
}